import { DetailTitleComponent, CustomSelect, RadioGroup } from '@/shared/ui/components';
import { SUPPORT_URL_REQUEST_TYPE } from '@/shared/config';
import classNames from 'classnames';
import { t } from 'ttag';
import AbortStudy from './submitARequest/abortStudy';
import BillingQuestions from './submitARequest/billingQuestions';
import ChangeStudyInformation from './submitARequest/changeStudyInformation';
import ChangeStudyType from './submitARequest/changeStudyType/changeStudyType';
import Other from './submitARequest/other';
import ReportRequest from './submitARequest/reportRequest';
import WebPortalIssues from './submitARequest/webPortalIssues';
import { useNewSupportRequest } from './useNewSupportRequest';
import { CATEGORY_LIST, OBJECT_CATEGORY_LIST } from '../config/newSupportRequest';
import './newSupportRequest.scss';

/**
 * Page for submitting a new support request to Biotricity. Reads category and facility state from useNewSupportRequest, shows a facility CustomSelect only when more than one supported facility is available, offers the CATEGORY_LIST radio group to pick a request type, applies a scroll shadow to the title bar via state.isScroll, and renders the matching category form below through renderRequestForm.
 *
 * @returns Page element for creating a support request.
 */
const NewSupportRequest = () => {
  const { state, setLoadingPage, backToPrevious, onChange, onChangeCategory } =
    useNewSupportRequest();

  /**
   * Shows or clears the page loading overlay on behalf of the support request sub-forms.
   * Passes a string through as the loading text and maps a boolean to undefined before calling
   * the loading store's setter.
   *
   * @param loading - Loading text to show, or a boolean flag from the sub-form.
   * @returns Nothing.
   */
  const handleSetLoadingPage = (loading: string | boolean) =>
    setLoadingPage(typeof loading === 'string' ? loading : undefined);

  /**
   * Selects the request form matching the support request category chosen by the user. Switches state.category over the SUPPORT_URL_REQUEST_TYPE values for change study type, abort study, report request, change study information, web portal issues, billing questions and others, passing each form its mapped OBJECT_CATEGORY_LIST entry, the selected facility, setLoadingPage and backToPrevious, and returns null when no category is selected.
   *
   * @returns Category-specific request form element, or null when no category matches.
   */
  const renderRequestForm = () => {
    switch (state.category) {
      case SUPPORT_URL_REQUEST_TYPE.changeStudyType: {
        return (
          <ChangeStudyType
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.abortStudy: {
        return (
          <AbortStudy
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.reportRequest: {
        return (
          <ReportRequest
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.changeStudyInformation: {
        return (
          <ChangeStudyInformation
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.webPortalIssues: {
        return (
          <WebPortalIssues
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.billingQuestions: {
        return (
          <BillingQuestions
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      case SUPPORT_URL_REQUEST_TYPE.others: {
        return (
          <Other
            category={OBJECT_CATEGORY_LIST[state.category]}
            selectedFacility={state.selectedFacility}
            setLoadingPage={handleSetLoadingPage}
            backToPrevious={backToPrevious}
          />
        );
      }
      default: {
        return null;
      }
    }
  };

  return (
    <div className={classNames('custom-page new-support-request')}>
      <div className={classNames('top-container', state.isScroll ? '--has-box-shadow' : '')}>
        <DetailTitleComponent
          isLoadedData={state.isLoadedData}
          linkGoBack="/support"
          title={t`New support request`}
        />
      </div>
      <div className="custom-body new-support-request-body">
        <div className="new-support-request-content">
          {state.formattedSupportFacilities?.length > 1 && (
            <>
              <span className="title-content">{t`Facility`}</span>
              <CustomSelect
                className="select-facilities-select-input"
                name="selectedFacility"
                options={state.formattedSupportFacilities}
                value={state.selectedFacility}
                onChange={onChange}
                allowClear={false}
                size="middle"
                aria-label={t`Facility`}
              />
            </>
          )}
          <span className="title-content">{t`Request category`}</span>
          <div className="category-list">
            <RadioGroup
              name="category"
              onChange={onChangeCategory}
              options={CATEGORY_LIST}
              value={state.category}
              size="middle"
            />
          </div>
          {renderRequestForm()}
        </div>
      </div>
    </div>
  );
};

export default NewSupportRequest;
